// Owner-only Review Audit — READ-ONLY scan of every task in Review status,
// with an explicit confirm-gated "Repair Selected Issues" action.
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Loader2, ShieldCheck, AlertTriangle, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('stellar_token')}` });

export const ReviewAuditDialog = ({ open, onOpenChange, workspaceId }) => {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [repairPlan, setRepairPlan] = useState(null);
  const [repairing, setRepairing] = useState(false);

  const runAudit = async () => {
    setLoading(true);
    setRepairPlan(null);
    setSelected(new Set());
    try {
      const r = await axios.get(`${API}/api/admin/review-audit`, {
        params: { workspace_id: workspaceId }, headers: authHeaders(),
      });
      setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const previewRepair = async () => {
    setRepairing(true);
    try {
      const r = await axios.post(`${API}/api/admin/review-audit/repair`,
        { workspace_id: workspaceId, issue_ids: [...selected], confirm: false },
        { headers: authHeaders() });
      setRepairPlan(r.data.plan);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally {
      setRepairing(false);
    }
  };

  const confirmRepair = async () => {
    setRepairing(true);
    try {
      const r = await axios.post(`${API}/api/admin/review-audit/repair`,
        { workspace_id: workspaceId, issue_ids: [...selected], confirm: true },
        { headers: authHeaders() });
      const fixed = (r.data.applied || []).filter(p => p.repaired).length;
      toast.success(`Repair complete — ${fixed} issue(s) fixed`);
      setRepairPlan(null);
      runAudit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally {
      setRepairing(false);
    }
  };

  const s = report?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="review-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[15px]">
            <ShieldCheck className="h-4 w-4 text-gold" /> Review Audit (Owner only)
          </DialogTitle>
          <DialogDescription className="text-[12px]">
            Read-only scan of every task in Review: submission links, attachment storage health,
            and reviewer access. Nothing is changed unless you explicitly confirm a repair.
          </DialogDescription>
        </DialogHeader>

        {!report && (
          <Button onClick={runAudit} disabled={loading} data-testid="run-review-audit-btn" className="w-fit">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null}
            Run read-only audit
          </Button>
        )}

        {report && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[12px]" data-testid="review-audit-summary">
              <div className="rounded-lg border border-border/20 p-2.5"><div className="text-[18px] font-bold">{s.tasks_in_review_scanned}</div>Tasks in Review scanned</div>
              <div className="rounded-lg border border-border/20 p-2.5"><div className="text-[18px] font-bold">{s.attachments_checked}</div>Attachments verified in storage</div>
              <div className="rounded-lg border border-border/20 p-2.5"><div className={`text-[18px] font-bold ${s.total_issues ? 'text-red-400' : 'text-emerald-400'}`}>{s.total_issues}</div>Issues found ({s.repairable_issues} repairable)</div>
            </div>

            {s.total_issues === 0 ? (
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-[12px] text-emerald-300" data-testid="review-audit-clean">
                All tasks in Review have valid submissions, every attachment is reachable in storage,
                and all Creators, Assignees and Accountable users have access.
              </div>
            ) : (
              <div className="space-y-2" data-testid="review-audit-issues">
                {report.issues.map(i => (
                  <div key={i.issue_id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2.5 flex gap-2.5 items-start">
                    {i.safe_to_repair && (
                      <Checkbox checked={selected.has(i.issue_id)} onCheckedChange={() => toggle(i.issue_id)}
                        className="mt-0.5" data-testid={`audit-issue-check-${i.issue_id}`} />
                    )}
                    <div className="text-[12px] min-w-0">
                      <div className="font-medium flex items-center gap-1.5">
                        <AlertTriangle className="h-3 w-3 text-amber-400 shrink-0" />
                        {i.task}{i.client ? ` · ${i.client}` : ''}{i.version ? ` · V${i.version}` : ''}
                      </div>
                      {i.file && <div className="text-muted-foreground/70">File: {i.file} ({i.mime_type}) · uploaded by {i.uploader}</div>}
                      <div className="text-amber-300/90 mt-0.5">{i.problem}</div>
                      <div className="text-muted-foreground/60 mt-0.5">
                        <Badge variant="outline" className={`text-[10px] mr-1.5 ${i.safe_to_repair ? 'border-emerald-500/40 text-emerald-300' : 'border-red-500/40 text-red-300'}`}>
                          {i.safe_to_repair ? 'Safe to repair' : 'Not repairable'}
                        </Badge>
                        {i.repair_action}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {repairPlan && (
              <div className="rounded-lg border border-border/30 p-3 space-y-2" data-testid="repair-plan-preview">
                <div className="text-[12px] font-medium">Repair preview — confirm to apply:</div>
                {repairPlan.map((p, idx) => (
                  <div key={idx} className="text-[11px] text-muted-foreground/80">
                    {p.action === 'revert_status'
                      ? `"${p.task}" — status ${p.before} → ${p.after}`
                      : `${p.issue_id}: skipped (${p.reason})`}
                  </div>
                ))}
                <Button size="sm" onClick={confirmRepair} disabled={repairing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-repair-btn">
                  {repairing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wrench className="h-3.5 w-3.5 mr-1" />}
                  Confirm & repair
                </Button>
              </div>
            )}

            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={runAudit} disabled={loading} data-testid="rerun-audit-btn">
                {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Re-run audit
              </Button>
              {selected.size > 0 && !repairPlan && (
                <Button size="sm" onClick={previewRepair} disabled={repairing} data-testid="repair-selected-btn">
                  {repairing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Wrench className="h-3.5 w-3.5 mr-1" />}
                  Repair selected ({selected.size})
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
